import { useEffect, useState, useMemo } from 'react';
import Head from 'expo-router/head';
import { Platform } from 'react-native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { ChevronLeft, MapPin, Trophy, TrendingUp, Swords, GitCompare } from 'lucide-react-native';
import { useAuth } from '@/contexts/AuthContext';
import { getUserDuelStats, type DuelStats } from '@/lib/duelStats';
import { getProfileOGTags } from '@/lib/shareLinks';
import FounderBadge from '@/components/FounderBadge';

interface PublicProfile {
  id: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  home_gym_name: string | null;
  gym_id: string | null;
  duel_standard_strength_kg: number | null;
  current_rank: string | null;
  units: string | null;
  training_goals: string[] | null;
  created_at: string | null;
}

export default function PublicProfileScreen() {
  const { handle } = useLocalSearchParams<{ handle: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const { profile: currentUserProfile } = useAuth();
  const c = theme.colors;
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [duelStats, setDuelStats] = useState<DuelStats | null>(null);
  const [totalWorkouts, setTotalWorkouts] = useState<number>(0);
  const [totalVolume, setTotalVolume] = useState<number>(0);
  const [memberSince, setMemberSince] = useState<string>('');

  useEffect(() => {
    if (!handle) return;
    (async () => {
      setLoading(true);
      // Try public view first (accessible without RLS), fallback to profiles
      // Try multiple approaches to get profile data (RLS may block cross-user reads)
      let profileData: any = null;

      // Approach 1: Direct profiles query (works for own profile, may work if RLS allows)
      const { data: directData } = await supabase
        .from('profiles')
        .select('id, handle, display_name, avatar_url, bio, home_gym_name, gym_id, duel_standard_strength_kg, current_rank, units, created_at, lifting_score, training_goals')
        .eq('handle', handle)
        .maybeSingle();

      if (directData) {
        profileData = directData;
      } else {
        // Approach 2: Public view (limited fields but no RLS)
        const { data: viewData } = await supabase
          .from('profiles')
          .select('id, handle, display_name, avatar_url, gym_id')
          .eq('handle', handle)
          .maybeSingle();

        if (viewData) {
          profileData = {
            id: viewData.id,
            handle: viewData.handle,
            display_name: viewData.display_name,
            avatar_url: viewData.avatar_url,
            home_gym_name: null,
            duel_standard_strength_kg: null,
            current_rank: null,
            units: null,
            created_at: null,
            bio: null,
            lifting_score: null,
            training_goals: null,
          };

          // Try to get strength score from leaderboard view
          const { data: lbData } = await supabase
            .from('public_leaderboard_global_v1')
            .select('strength_score')
            .eq('id', viewData.id)
            .maybeSingle();
          if (lbData?.strength_score) {
            profileData.duel_standard_strength_kg = lbData.strength_score;
          }
        }
      }

      if (profileData) {
        setProfile(profileData as PublicProfile);

        // Fetch duel stats
        getUserDuelStats(profileData.id).then(setDuelStats);

        // Fetch workout stats
        const { count } = await supabase
          .from('workouts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', profileData.id);
        setTotalWorkouts(count ?? 0);

        // Compute total volume
        const { data: workouts } = await supabase
          .from('workouts')
          .select('payload')
          .eq('user_id', profileData.id);
        if (workouts) {
          let vol = 0;
          workouts.forEach((w: any) => {
            const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
            (payload?.exercises ?? []).forEach((ex: any) => {
              (ex.sets ?? []).forEach((s: any) => {
                vol += (Number(s.weight) || 0) * (Number(s.reps) || 0);
              });
            });
          });
          setTotalVolume(vol);
        }

        // Member since
        if (profileData.created_at) {
          const d = new Date(profileData.created_at);
          setMemberSince(d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        }
      }
      setLoading(false);
    })();
  }, [handle]);

  const displayName = profile?.display_name
    || profile?.handle
    || 'Member';

  const strengthScore = profile?.duel_standard_strength_kg;
  const hasStrength = typeof strengthScore === 'number' && isFinite(strengthScore) && strengthScore > 0;
  const units = profile?.units ?? 'lbs';

  const formatVolume = (v: number): string => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${Math.round(v / 1000)}K`;
    return `${Math.round(v)}`;
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <Stack.Screen options={{ headerShown: false }} />

      {/* OG Meta Tags for web sharing only — suppress on native to avoid Expo Head warning */}
      {profile && Platform.OS === 'web' && (
        <Head>
          <title>{`${displayName} (@${profile.handle}) — DUEL`}</title>
          <meta name="description" content={`${displayName} on DUEL — Do U Even Lift. Challenge them to a duel.`} />
          <meta property="og:title" content={`${displayName} (@${profile.handle}) — DUEL`} />
          <meta property="og:description" content={`${displayName} on DUEL — Do U Even Lift.${strengthScore ? ` Strength: ${Math.round(strengthScore)}` : ''}`} />
          <meta property="og:url" content={`https://douevenlift.com/u/${profile.handle}`} />
          <meta property="og:type" content="profile" />
          {profile.avatar_url && <meta property="og:image" content={profile.avatar_url} />}
          <meta name="twitter:card" content="summary" />
        </Head>
      )}

      {/* Header with back button */}
      <View style={[styles.navBar, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn} hitSlop={12}>
          <ChevronLeft size={22} color={c.textSecondary} />
        </TouchableOpacity>
        <Text style={[styles.navTitle, { color: c.textSecondary }]}>Profile</Text>
        <View style={{ width: 30 }} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={c.accent} />
        </View>
      ) : !profile ? (
        <View style={styles.center}>
          <Text style={{ color: c.textSecondary, fontSize: 16 }}>User not found</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {/* Avatar + Name */}
          <View style={styles.profileHeader}>
            {profile.avatar_url ? (
              <Image source={{ uri: profile.avatar_url }} style={styles.avatar} />
            ) : (
              <View style={[styles.avatarFallback, { backgroundColor: c.surfaceVariant }]}>
                <Text style={[styles.avatarInitial, { color: c.textSecondary }]}>
                  {displayName.charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Text style={[styles.displayName, { color: c.text }]}>{displayName}</Text>
              <FounderBadge createdAt={profile.created_at} />
            </View>
            {profile.handle && (
              <Text style={[styles.handle, { color: c.textTertiary }]}>@{profile.handle}</Text>
            )}
            {profile.bio ? (
              <Text style={[styles.bio, { color: c.textSecondary }]}>{profile.bio}</Text>
            ) : null}
            {profile.training_goals && profile.training_goals.length > 0 && (
              <View style={styles.goalsRow}>
                {profile.training_goals.map((goal) => (
                  <View key={goal} style={[styles.goalTag, { backgroundColor: c.surfaceVariant }]}>
                    <Text style={[styles.goalTagText, { color: c.textSecondary }]}>{goal}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Compare + Challenge buttons */}
          {currentUserProfile?.id && profile.id !== currentUserProfile.id && (
            <View style={styles.actionRow}>
              <TouchableOpacity
                style={[styles.compareBtn, { borderColor: c.border }]}
                onPress={() => router.push({
                  pathname: '/compare' as any,
                  params: { userId1: currentUserProfile.id, userId2: profile.id },
                })}
              >
                <GitCompare size={14} color={c.accent} />
                <Text style={[styles.compareBtnText, { color: c.accent }]}>Compare</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.challengeBtn, { backgroundColor: c.accent }]}
                onPress={() => router.push({
                  pathname: '/(tabs)/duels' as any,
                  params: { challengeUserId: profile.id, challengeHandle: profile.handle },
                })}
                accessibilityRole="button"
                accessibilityLabel={`Challenge ${displayName} to a duel`}
              >
                <Swords size={14} color={c.accentForeground} />
                <Text style={[styles.challengeBtnText, { color: c.accentForeground }]}>Challenge</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Info Row */}
          <View style={styles.infoRow}>
            {profile.home_gym_name && (
              <TouchableOpacity
                style={styles.infoItem}
                disabled={!profile.gym_id}
                onPress={() => profile.gym_id && router.push(`/gym/${profile.gym_id}` as any)}
                activeOpacity={0.7}
              >
                <MapPin size={14} color={c.textTertiary} />
                <Text style={[styles.infoText, { color: profile.gym_id ? c.accent : c.textSecondary }]}>{profile.home_gym_name}</Text>
              </TouchableOpacity>
            )}
            {memberSince ? (
              <Text style={[styles.infoText, { color: c.textTertiary }]}>Member since {memberSince}</Text>
            ) : null}
          </View>

          {/* Stats Cards */}
          <View style={styles.statsRow}>
            {hasStrength && (
              <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                <Trophy size={18} color={c.accent} />
                <Text style={[styles.statValue, { color: c.text }]}>{Math.round(strengthScore!)}</Text>
                <Text style={[styles.statLabel, { color: c.textSecondary }]}>Strength Score</Text>
              </View>
            )}
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <TrendingUp size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{formatVolume(totalVolume)}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Total Volume ({units})</Text>
            </View>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Swords size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>
                {duelStats ? `${duelStats.wins}W-${duelStats.losses}L` : '0W-0L'}
              </Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Duel Record</Text>
            </View>
          </View>

          {/* Workout Summary */}
          <View style={[styles.section, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.sectionTitle, { color: c.textTertiary }]}>ACTIVITY</Text>
            <View style={styles.sectionRow}>
              <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>Total Workouts</Text>
              <Text style={[styles.sectionValue, { color: c.text }]}>{totalWorkouts}</Text>
            </View>
            <View style={styles.sectionRow}>
              <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>Total Volume</Text>
              <Text style={[styles.sectionValue, { color: c.text }]}>{Math.round(totalVolume).toLocaleString()} {units}</Text>
            </View>
            {duelStats && duelStats.total_duels > 0 && (
              <View style={styles.sectionRow}>
                <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>Win Rate</Text>
                <Text style={[styles.sectionValue, { color: c.text }]}>{duelStats.win_rate_percentage}%</Text>
              </View>
            )}
            {profile.current_rank && (
              <View style={styles.sectionRow}>
                <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>Rank</Text>
                <Text style={[styles.sectionValue, { color: c.text }]}>{profile.current_rank}</Text>
              </View>
            )}
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  navBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backBtn: {
    padding: 4,
  },
  navTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  profileHeader: {
    alignItems: 'center',
    marginBottom: 20,
    gap: 6,
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: 4,
    marginBottom: 8,
  },
  avatarFallback: {
    width: 88,
    height: 88,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  avatarInitial: {
    fontSize: 32,
    fontWeight: '800',
  },
  displayName: {
    fontSize: 22,
    fontWeight: '800',
  },
  handle: {
    fontSize: 14,
    fontWeight: '600',
  },
  bio: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 20,
    marginTop: 8,
    paddingHorizontal: 16,
  },
  goalsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    marginTop: 10,
  },
  goalTag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
  },
  goalTagText: {
    fontSize: 11,
    fontWeight: '600',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  infoText: {
    fontSize: 13,
    fontWeight: '500',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  statCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    alignItems: 'center',
    gap: 4,
  },
  statValue: {
    fontSize: 18,
    fontWeight: '900',
  },
  statLabel: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
  },
  section: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 12,
  },
  sectionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(128,128,128,0.15)',
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  sectionValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  actionRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 12,
    justifyContent: 'center',
  },
  compareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 0,
    borderWidth: 1,
  },
  compareBtnText: {
    fontSize: 13,
    fontWeight: '700',
  },
  challengeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 0,
  },
  challengeBtnText: {
    fontSize: 13,
    fontWeight: '700',
  },
});
